import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';

import { apiScopes, msalInstance } from '@/auth/msalConfig';
import { useEnvStore } from '@/env/envStore';
import { HUB_ENV_HEADER } from '@/env/types';

export class HubApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `${String(status)} ${statusText}`);
    this.name = 'HubApiError';
  }
}

/**
 * Acquire an access token for the hub-api scope. Uses the singleton MSAL
 * instance from `auth/msalConfig` so we share the in-memory cache with the
 * React tree. Falls back to redirect on `InteractionRequiredAuthError` —
 * MSAL's silent path can fail mid-session if the refresh token is revoked
 * or the consent state changes; in that case the only recovery is to send
 * the user back through the Entra UI.
 *
 * The redirect path doesn't return — `acquireTokenRedirect` triggers a full
 * page navigation. We throw a HubApiError after kicking it off so the calling
 * fetch promise rejects cleanly instead of hanging.
 */
async function acquireApiAccessToken(): Promise<string> {
  // Same rule as main.tsx's bootstrap restore — never guess between cached
  // accounts. By the time this runs the React tree is past
  // <MsalAuthenticationTemplate>, so an active account should always be
  // set; the fallbacks are defensive in case a token acquire fires before
  // bootstrap finished or after a sign-out race.
  //
  //   - active account set → use it
  //   - exactly one cached account → safe to auto-select
  //   - otherwise (0 or >1) → throw so the caller surfaces "Unauthorized"
  //     rather than picking a wrong identity
  const active = msalInstance.getActiveAccount();
  let account: AccountInfo | null = active;
  if (account === null) {
    const allAccounts = msalInstance.getAllAccounts();
    if (allAccounts.length === 1) {
      account = allAccounts[0];
    }
  }
  if (account === null) {
    throw new HubApiError(401, 'Unauthorized', null, 'No active signed-in account');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [...apiScopes],
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Carry the same account into the redirect so MSAL doesn't prompt for
      // account selection (or silently pick a different cached account) when
      // the user has signed into more than one. Also bump active-account so
      // any concurrent in-flight `acquireTokenSilent` calls converge on this
      // identity for the duration of the redirect.
      msalInstance.setActiveAccount(account);
      await msalInstance.acquireTokenRedirect({
        account,
        scopes: [...apiScopes],
      });
      // Page is navigating; rejection just keeps the callsite happy.
      throw new HubApiError(0, 'Interaction required', null, 'Sign-in redirect started');
    }
    throw err;
  }
}

interface RequestInitWithJson extends RequestInit {
  json?: unknown;
  searchParams?: Record<string, string | number | boolean | undefined>;
}

// Raw VITE_HUB_API_URL (trimmed). May be:
//   - absolute (`http://localhost:5000` in dev when running the SPA against
//     a standalone API)
//   - relative (`/` in prod — nginx proxies /api/* to ca-hub-api; same
//     origin from the browser's POV)
//   - empty (fallback to same-origin)
//
// We resolve this once to a concrete absolute base URL (`apiBaseUrl`) so
// downstream code doesn't have to re-handle the relative/empty cases. Note
// that `new URL(relative, base)` throws unless `base` is absolute, which is
// why we always seed with `window.location.origin`.
const rawApiUrl = (() => {
  const env: unknown = import.meta.env.VITE_HUB_API_URL;
  if (typeof env === 'string' && env.length > 0) {
    return env.replace(/\/+$/, '');
  }
  return '';
})();

const apiBaseUrl: URL = rawApiUrl.length > 0
  ? new URL(rawApiUrl, window.location.origin)
  : new URL(window.location.origin);

/** Origin we'll ever attach a Bearer token to — derived from `apiBaseUrl`.
 *  Used by `hubFetch` to refuse to send the hub-api access token to any
 *  other host (defense against a caller passing an absolute URL to a
 *  different origin). */
const hubApiOrigin = apiBaseUrl.origin;

function buildUrl(path: string, search?: RequestInitWithJson['searchParams']): URL {
  // `apiBaseUrl` is always absolute, so this never throws on a relative path.
  const url = new URL(path, apiBaseUrl);
  if (search) {
    for (const [key, value] of Object.entries(search)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url;
}

export async function hubFetch<T>(path: string, init: RequestInitWithJson = {}): Promise<T> {
  const env = useEnvStore.getState().env;
  const headers = new Headers(init.headers);
  headers.set(HUB_ENV_HEADER, env);

  const url = buildUrl(path, init.searchParams);

  // Defense against an accidental token leak: only attach the hub-api
  // Bearer if the resolved URL's origin matches the configured hub-api
  // origin. Without this guard, a caller passing an absolute URL (e.g. a
  // typo'd `https://malicious.example/...` or a copy-paste from an
  // external doc) would send our access token to that host.
  if (url.origin !== hubApiOrigin) {
    throw new HubApiError(
      0,
      'CrossOriginRefused',
      null,
      `Refusing to attach hub-api token to a different origin. ` +
        `Got ${url.origin}; expected ${hubApiOrigin}.`,
    );
  }

  // Bearer token for the hub-api. Hub.Api/Program.cs has
  // FallbackPolicy = RequireAuthenticatedUser, so every /api/* call needs
  // one — there's no anonymous path here.
  const token = await acquireApiAccessToken();
  headers.set('Authorization', `Bearer ${token}`);
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const response = await fetch(url, {
    ...init,
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
    // No cookies in the auth flow — MSAL uses Bearer tokens + sessionStorage —
    // so `credentials: 'include'` would only add CORS friction without buying
    // anything. The API doesn't read cookies anyway.
    credentials: 'same-origin',
  });

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new HubApiError(response.status, response.statusText, payload);
  }

  return payload as T;
}
